"use client"

import { useMemo, useState } from "react"
import { Play, Loader2 } from "lucide-react"

import type { DashboardOrder } from "./index"

/**
 * Client component for /admin/paiements.
 *
 * Shadcn-styled (using the same `--adis-*` CSS variables the rest of
 * the admin and storefront share) so this dashboard reads as part of
 * ADIS 95, not as the generic Payload chrome.
 *
 * Features:
 *   • KPI strip: total / paid / pending / cancelled / revenue
 *   • Status filter pills (All / Paid / Pending / Cancelled)
 *   • Search by order #, customer email, or customer name
 *   • Status pill per row using the storefront colour vocabulary
 *     (forest = success, amber = waiting, slate = archived)
 *   • Each row links to the classic Payload edit screen so admins can
 *     update notes/shipping address without leaving the data model
 *   • Manual "Reconcile pending" button that triggers the same logic
 *     as the hourly cron — useful right after a deploy when you want
 *     to immediately flush stale pending orders.
 *
 * Money formatting follows fr-FR locale + EUR currency convention so
 * "1 234,56 €" appears identically to the public storefront.
 */

const eur = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
})

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}

const STATUS_LABELS: Record<string, string> = {
  pending: "En attente",
  paid: "Payée",
  processing: "En préparation",
  shipped: "Expédiée",
  delivered: "Livrée",
  cancelled: "Annulée",
}

type StatusFilter = "all" | "pending" | "paid" | "cancelled"

export function PaymentsClient({
  orders,
  stats,
}: {
  orders: DashboardOrder[]
  stats: {
    total: number
    paid: number
    pending: number
    cancelled: number
    revenue: number
  }
}) {
  const [filter, setFilter] = useState<StatusFilter>("all")
  const [query, setQuery] = useState("")
  const [reconciling, setReconciling] = useState(false)
  const [reconcileResult, setReconcileResult] = useState<string | null>(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return orders.filter((o) => {
      if (filter === "paid" && o.status !== "paid") return false
      if (filter === "pending" && o.status !== "pending") return false
      if (filter === "cancelled" && o.status !== "cancelled") return false
      if (!q) return true
      return (
        o.orderNumber.toLowerCase().includes(q) ||
        (o.customerEmail || "").toLowerCase().includes(q) ||
        (o.customerName || "").toLowerCase().includes(q)
      )
    })
  }, [orders, filter, query])

  async function handleReconcile() {
    setReconciling(true)
    setReconcileResult(null)
    try {
      const res = await fetch("/api/admin/expire-pending-orders", {
        method: "POST",
        credentials: "include",
      })
      const json = (await res.json()) as {
        ok?: boolean
        flipped?: number
        cancelled?: number
        scanned?: number
        error?: string
      }
      if (json.ok) {
        setReconcileResult(
          `Réconciliation OK — ${json.flipped ?? 0} marquée(s) payée(s), ${json.cancelled ?? 0} annulée(s) sur ${json.scanned ?? 0} en attente.`,
        )
        // Soft refresh after 2s so the new statuses appear without a full reload.
        setTimeout(() => window.location.reload(), 2000)
      } else {
        setReconcileResult(`Erreur : ${json.error ?? "inconnue"}`)
      }
    } catch (err) {
      setReconcileResult(`Erreur réseau : ${(err as Error).message}`)
    } finally {
      setReconciling(false)
    }
  }

  return (
    <div className="adis-pay">
      <header className="adis-pay__header">
        <div>
          <h1 className="adis-pay__title">Paiements & commandes</h1>
          <p className="adis-pay__sub">
            Vue d&apos;ensemble en direct, dans le même habillage que le
            site public. Cliquez sur une ligne pour ouvrir l&apos;édition
            Payload classique.
          </p>
        </div>
        <button
          type="button"
          className="adis-pay__reconcile"
          onClick={handleReconcile}
          disabled={reconciling}
        >
          {reconciling ? (
            <>
              <Loader2 className="adis-pay__reconcile-icon" size={14} aria-hidden />
              <span>Réconciliation en cours</span>
            </>
          ) : (
            <>
              <Play className="adis-pay__reconcile-icon" size={14} aria-hidden />
              <span>Réconcilier les paiements en attente</span>
            </>
          )}
        </button>
      </header>

      {reconcileResult ? (
        <div className="adis-pay__notice">{reconcileResult}</div>
      ) : null}

      <div className="adis-pay__kpis">
        {[
          { label: "Total", value: stats.total },
          { label: "Payées", value: stats.paid, tone: "success" as const },
          { label: "En attente", value: stats.pending, tone: "warn" as const },
          { label: "Annulées", value: stats.cancelled, tone: "muted" as const },
          {
            label: "Chiffre d'affaires HT",
            value: eur.format(stats.revenue),
            isMoney: true,
          },
        ].map((kpi) => (
          <div
            key={kpi.label}
            className={
              "adis-pay__kpi" +
              (kpi.tone ? ` adis-pay__kpi--${kpi.tone}` : "")
            }
          >
            <span className="adis-pay__kpi-value">
              {typeof kpi.value === "number"
                ? kpi.value.toLocaleString("fr-FR")
                : kpi.value}
            </span>
            <span className="adis-pay__kpi-label">{kpi.label}</span>
          </div>
        ))}
      </div>

      <div className="adis-pay__toolbar">
        <div className="adis-pay__filters" role="tablist">
          {(
            [
              { id: "all", label: "Toutes" },
              { id: "paid", label: "Payées" },
              { id: "pending", label: "En attente" },
              { id: "cancelled", label: "Annulées" },
            ] as Array<{ id: StatusFilter; label: string }>
          ).map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={
                "adis-pay__pill" +
                (filter === f.id ? " adis-pay__pill--active" : "")
              }
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder="Rechercher par n°, email, nom…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="adis-pay__search"
          aria-label="Rechercher dans les commandes"
        />
      </div>

      <div className="adis-pay__table-wrap" role="region" aria-label="Liste des commandes">
        {filtered.length === 0 ? (
          <div className="adis-pay__empty">
            {orders.length === 0
              ? "Aucune commande pour l'instant — les paiements Stripe apparaîtront ici dès le premier achat."
              : "Aucune commande ne correspond à ces filtres."}
          </div>
        ) : (
          <table className="adis-pay__table">
            <thead>
              <tr>
                <th scope="col">N°</th>
                <th scope="col">Client</th>
                <th scope="col">Statut</th>
                <th scope="col" className="adis-pay__num">Articles</th>
                <th scope="col" className="adis-pay__num">Total</th>
                <th scope="col">Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={String(o.id)}>
                  <td>
                    <a
                      href={`/admin/collections/orders/${o.id}`}
                      className="adis-pay__link"
                    >
                      {o.orderNumber}
                    </a>
                  </td>
                  <td>
                    <div className="adis-pay__customer">
                      {o.customerName ? (
                        <span className="adis-pay__customer-name">
                          {o.customerName}
                        </span>
                      ) : null}
                      <span className="adis-pay__customer-email">
                        {o.customerEmail}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`adis-pay__status adis-pay__status--${o.status}`}
                    >
                      {STATUS_LABELS[o.status] ?? o.status}
                    </span>
                  </td>
                  <td className="adis-pay__num">{o.itemCount}</td>
                  <td className="adis-pay__num adis-pay__total">
                    {eur.format(o.total)}
                  </td>
                  <td>{formatDate(o.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="adis-pay__footnote">
        Les commandes en attente de paiement sont automatiquement
        réconciliées avec Stripe une fois par jour (cron Vercel à 04:30
        UTC) — les sessions Stripe expirent au bout de 24&nbsp;h. La
        page « merci » ré-interroge Stripe à chaque chargement pour
        afficher le statut payé sans attendre. Vous pouvez forcer la
        réconciliation immédiatement avec le bouton ci-dessus.
      </p>
    </div>
  )
}
